import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Undo2, Eye, EyeOff } from 'lucide-react';

import NewAdminAuthService from '../../services/admin-auth-service';
import NewRoleService from '../../services/role-service';

import { useAuth } from '../../contexts/AuthContext';

import { showToast } from '../../components/ToastConfig';

function ProfileForm() {
  const Navigate = useNavigate();
  const [accountData, setAccountData] = useState(null);
  // const [roleOptions, setRoleOptions] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const { logout } = useAuth();
  
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    setValue,
    reset
  } = useForm({
    mode: 'onChange'
  });

  // 獲取角色列表
  // useEffect(() => {
  //   const fetchRoles = async () => {
  //     try {
  //       const res = await NewRoleService.search();
  //       console.log("res", res);
  //       setRoleOptions(res.data?.data);
  //     } catch (error) {
  //       console.error('Failed to fetch roles:', error);
  //       showToast.error('獲取角色列表失敗');
  //     }
  //   };

  //   fetchRoles();
  // }, []);

  // 獲取帳號資料
  useEffect(() => {
    const fetchAccountData = async () => {
      try {
        const data = await NewAdminAuthService.getProfile();
        setAccountData(data.data);
        
        // 設置表單預設值
        Object.keys(data.data).forEach(key => {
          setValue(key, data.data[key]);
        });
      } catch (error) {
        console.error('Failed to fetch account data:', error);
        if (error?.response?.status === 401 || error?.response?.status === 403) {
          // showToast.error('無權訪問此頁面');
          logout();
        } else {
          showToast.error('獲取帳號資料失敗');
        }
      }
    };

    fetchAccountData();
  }, [setValue, Navigate]);

  const onSubmit = async (data) => {
    // 如果有輸入密碼，檢查是否與確認密碼相符
    if (data.newPassword && data.newPassword !== data.newPasswordConfirm) {
      showToast.error('密碼與確認密碼不符');
      return;
    }

    // 如果沒有輸入密碼，從提交數據中移除密碼相關欄位
    if (!data.newPassword.trim()) {
      delete data.newPassword;
      delete data.newPasswordConfirm;
    }

    delete data.email;
    delete data.role;

    try {
      let res;
      res = await NewAdminAuthService.update(data);
      showToast.success('帳號更新成功');

      // setTimeout(() => {
      //   Navigate('/account');
      // }, 1000);
    } catch (error) {
      console.error('Form submission failed:', error);
      showToast.error(error?.response?.data?.message || '提交失敗，請稍後再試');
    }
  };

  return (
    <div className="container mx-auto p-6">
      {/* <Link to="/account" className="text-blue-500 hover:text-blue-600 group flex items-center mb-5 w-fit p-3 -ml-3">
        <Undo2 size={36} className="text-blue-500 group-hover:text-blue-600 mr-1" />  
        <span className="font-bold">返回</span>
      </Link> */}

      <div className="mx-auto">
        <h1 className="text-2xl font-bold mb-6">
          個人資料
        </h1>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* 姓名 */}
            <div className="space-y-1">
              <label className="block text-sm font-medium">
                姓名
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                {...register('name', { 
                  required: '姓名為必填欄位'
                })}
                className={`w-full p-2 border rounded ${errors.name ? 'border-red-500' : 'border-gray-300'}`}
              />
              {errors.name && (
                <p className="text-red-500 text-sm">{errors.name.message}</p>
              )}
            </div>

            {/* Email */}
            <div className="space-y-1">
              <label className="block text-sm font-medium">
                Email
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                {...register('email')}
                disabled
                className={`w-full p-2 border rounded border-gray-300 bg-gray-50`}
              />
            </div>

            {/* 職稱 */}
            <div className="space-y-1">
              <label className="block text-sm font-medium">
                職稱
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                {...register('jobTitle', { 
                  required: '職稱為必填欄位'
                })}
                className={`w-full p-2 border rounded ${errors.jobTitle ? 'border-red-500' : 'border-gray-300'}`}
              />
              {errors.jobTitle && (
                <p className="text-red-500 text-sm">{errors.jobTitle.message}</p>
              )}
            </div>

            {/* 角色 */}
            <div className="space-y-1">
              <label className="block text-sm font-medium">
                角色
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                {...register('role')}
                disabled
                className={`w-full p-2 border rounded border-gray-300 bg-gray-50`}
                defaultValue={accountData?.roles[0]}
              />
              {errors.role && (
                <p className="text-red-500 text-sm">{errors.role.message}</p>
              )}
            </div>

            {/* 密碼 */}
            <div className="space-y-1">
              <label className="block text-sm font-medium">
                密碼
                <span className="text-red-500 text-xs ml-2">
                  (若不修改請留空)
                </span>
              </label>
              <div className="relative">
                <input 
                  type={showPassword ? "password" : "text"}
                  {...register('newPassword', {
                    pattern: {
                      value: /^.{6,}$/i,
                      message: '密碼至少需六位數'
                    }
                  })}
                  className="w-full p-2 border rounded border-gray-300"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {errors.newPassword && (
                <p className="text-red-500 text-sm">{errors.newPassword.message}</p>
              )}
            </div>

            {/* 確認密碼 */}
            <div className="space-y-1">
              <label className="block text-sm font-medium">
                確認密碼
                <span className="text-red-500 text-xs ml-2">
                  (若不修改請留空)
                </span>
              </label>
              <div className="relative">
                <input 
                  type={showPassword ? "password" : "text"}
                  {...register('newPasswordConfirm', {
                    pattern: {
                      value: /^.{6,}$/i,
                      message: '密碼至少需六位數'
                    }
                  })}
                  className="w-full p-2 border rounded border-gray-300"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {errors.newPasswordConfirm && (
              <p className="text-red-500 text-sm">{errors.newPasswordConfirm.message}</p>
            )}
            </div>
          </div>

          <div className="flex justify-end mt-8">
            <button
              type="submit"
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              disabled={isSubmitting}
            >
              {isSubmitting ? "提交中..." : "提交"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ProfileForm;